import React, { useState } from 'react';
import {
  ArrowRight,
  ChevronDown,
  UserCircle,
  Clock,
  ShieldCheck,
  FileText,
  Radio,
  TrendingUp,
  CheckCircle2,
  FileSearch,
  Send,
} from 'lucide-react';
import { Reveal, useSheen } from './Reveal';

const iconMap = { UserCircle, Clock, ShieldCheck, FileText };

const PIPELINE = [
  { sector: 'Construction & Infra', count: 34, pct: 78, color: '#F59E0B' },
  { sector: 'Chemicals & Pharma', count: 28, pct: 65, color: '#34D399' },
  { sector: 'IT & Engineering', count: 41, pct: 92, color: '#60A5FA' },
  { sector: 'Industrial Supplies', count: 45, pct: 100, color: '#A78BFA' },
];

const ACTIVITY = [
  { icon: CheckCircle2, color: 'text-emerald-400', text: '2 contracts awarded', when: 'This week' },
  { icon: Send, color: 'text-amber-400', text: '11 bids submitted', when: '7 days' },
  { icon: FileSearch, color: 'text-blue-400', text: '18 RFPs under review', when: 'Today' },
  { icon: TrendingUp, color: 'text-violet-400', text: '6 sectors trending up', when: 'This month' },
];

const Methodology = ({ steps, badges }) => {
  const [openIdx, setOpenIdx] = useState(0);
  const { onMouseMove } = useSheen();

  return (
    <section id="why" className="relative py-24 lg:py-32 px-6 lg:px-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: badges card */}
          <Reveal variant="left" className="space-y-6">
            <div className="flex flex-wrap gap-3">
              {badges.map((b, i) => {
                const Icon = iconMap[b.icon];
                return (
                  <div
                    key={i}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass-card text-sm text-slate-200"
                    style={{ transitionDelay: `${i * 60}ms` }}
                  >
                    <Icon className="w-4 h-4 text-amber-400" />
                    {b.label}
                  </div>
                );
              })}
            </div>

            <div onMouseMove={onMouseMove} className="card-sheen glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] tracking-[0.25em] uppercase text-amber-400 font-semibold">
                  Current Status
                </span>
                <Radio className="w-4 h-4 text-amber-400 pulse-dot" />
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                Active Bids This Month
              </div>
              <div className="text-3xl font-bold text-white mb-5">148 Tenders in Progress</div>

              {/* Sector pipeline breakdown */}
              <div className="pt-4 border-t border-white/5 space-y-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold mb-2">
                  Pipeline by Sector
                </div>
                {PIPELINE.map((p, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-slate-300">{p.sector}</span>
                      <span className="text-xs font-bold text-white">{p.count}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${p.pct}%`,
                          background: `linear-gradient(90deg, ${p.color} 0%, ${p.color}77 100%)`,
                          boxShadow: `0 0 6px ${p.color}55`,
                          transition: 'width 800ms cubic-bezier(0.22, 1, 0.36, 1)',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* This Week's Activity */}
            <div onMouseMove={onMouseMove} className="card-sheen glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] tracking-[0.25em] uppercase text-amber-400 font-semibold">
                  This Week's Activity
                </span>
                <span className="relative flex w-2 h-2">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-70 pulse-dot" />
                  <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
                </span>
              </div>
              <div className="space-y-3">
                {ACTIVITY.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                    <span className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                      <a.icon className={`w-4 h-4 ${a.color}`} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{a.text}</div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{a.when}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Right: methodology */}
          <Reveal variant="right">
            <div className="section-num mb-5">
              <span className="num">05</span>
              <span>Our Methodology</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-[1.05]">
              <span className="text-white">Engineered for </span>
              <span className="text-gradient-mixed">Enterprise Outcomes</span>
            </h2>
            <p className="text-slate-400 leading-relaxed mb-10 max-w-xl">
              We don't just submit bids — we architect winning submissions. Every engagement
              combines market intelligence, pricing discipline, and compliance precision to give
              corporate procurement teams a repeatable, auditable advantage.
            </p>

            <div className="space-y-3">
              {steps.map((s, i) => {
                const open = openIdx === i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    onMouseMove={onMouseMove}
                    className="card-sheen w-full text-left glass-card glass-card-hover rounded-2xl p-6 flex items-start gap-5"
                  >
                    <span className="text-amber-400 font-bold text-sm tracking-widest mt-1">{s.num}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="text-lg font-bold text-white">{s.title}</h3>
                        <ChevronDown
                          className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                            open ? 'rotate-180' : ''
                          }`}
                        />
                      </div>
                      <div
                        className={`grid transition-all duration-300 ${
                          open ? 'grid-rows-[1fr] mt-3 opacity-100' : 'grid-rows-[0fr] opacity-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="mt-10 inline-flex items-center gap-2 btn-blue rounded-full px-6 py-3.5 text-sm"
            >
              Book a Free Consultation
              <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Methodology;
